/**
 * ReporteComercial.jsx · v101.123.0 · Reporte diario de instalaciones (venta nueva) para Comercial, por sucursal.
 * Configuración (hora, destinatarios por sucursal / lista central, ventana), vista previa, descarga del Excel,
 * envío manual (reenvío / prueba a un correo) e historial de corridas.
 */
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Loader2, Download, Send, RefreshCw, Save, CheckCircle2, XCircle, Clock } from "lucide-react";

const ESTADO_CLS = {
  "Cancelado": "bg-rose-50 text-rose-700", "Agendado": "bg-emerald-50 text-emerald-700",
  "En proceso": "bg-sky-50 text-sky-700", "Nuevo": "bg-amber-50 text-amber-700",
  "Validado sin agenda": "bg-orange-50 text-orange-700", "Pendiente aprobación": "bg-slate-100 text-slate-600",
  "Instalado": "bg-teal-50 text-teal-700",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-EC", {
  day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil",
}) : "—");
const errMsg = (e, d) => String(e?.response?.data?.detail || d);

export default function ReporteComercial() {
  const [cfg, setCfg] = useState(null);
  const [estados, setEstados] = useState([]);
  const [meta, setMeta] = useState(null);
  const [prev, setPrev] = useState(null);
  const [suc, setSuc] = useState("");
  const [eq, setEq] = useState("");            // v101.248.0 · equipo de ventas
  const [destEqTxt, setDestEqTxt] = useState({});
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState("");
  const [destTxt, setDestTxt] = useState({});
  const [pruebaA, setPruebaA] = useState("");
  const [ctl, setCtl] = useState(null);

  const loadCfg = async () => {
    const { data } = await api.get("/v1/reporte-comercial/config");
    setCfg(data.config); setEstados(data.estados || []); setMeta(data.meta);
    const t = {};
    Object.entries(data.config.destinatarios || {}).forEach(([k, v]) => { t[k] = (v || []).join(", "); });
    if (!("*" in t)) t["*"] = "";
    setDestTxt(t);
    const te = {};
    Object.entries(data.config.destinatarios_equipo || {}).forEach(([k, v]) => { te[k] = (v || []).join(", "); });
    setDestEqTxt(te);
  };
  const loadRuns = async () => {
    const { data } = await api.get("/v1/reporte-comercial/corridas", { params: { limit: 15 } });
    setRuns(data.items || []);
  };
  const loadPrev = async (s = suc, e = eq) => {
    setBusy("prev");
    try {
      const { data } = await api.get("/v1/reporte-comercial/preview", { params: { sucursal: s || undefined, equipo: e || undefined, limit: 300 }, timeout: 180000 });
      setPrev(data);
    } catch (e) { toast.error(errMsg(e, "No se pudo generar la vista previa")); }
    finally { setBusy(""); }
  };
  useEffect(() => {
    loadCfg().catch((e) => toast.error(errMsg(e, "No se pudo cargar la configuración")));
    loadRuns().catch(() => {});
    loadPrev("");
    /* eslint-disable-next-line */
  }, []);

  const sucursales = useMemo(() => {
    const s = new Set(Object.keys(prev?.por_sucursal || {}));
    Object.keys(destTxt).forEach((k) => k !== "*" && s.add(k));
    return [...s].sort();
  }, [prev, destTxt]);

  const equipos = useMemo(() => {
    const s = new Set(Object.keys(prev?.por_equipo || {}));
    Object.keys(destEqTxt).forEach((k) => s.add(k));
    return [...s].sort();
  }, [prev, destEqTxt]);

  const save = async () => {
    setBusy("save");
    try {
      const destinatarios = {};
      Object.entries(destTxt).forEach(([k, v]) => {
        const list = String(v || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
        if (list.length || k === "*") destinatarios[k] = list;
      });
      const destinatarios_equipo = {};
      Object.entries(destEqTxt).forEach(([k, v]) => {
        const list = String(v || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
        if (list.length) destinatarios_equipo[k] = list;
      });
      const body = { ...cfg, destinatarios, destinatarios_equipo,
        control_pendiente: Array.isArray(cfg.control_pendiente) ? cfg.control_pendiente
          : String(cfg.control_pendiente || "").split(",").map((x) => x.trim()).filter(Boolean) };
      const { data } = await api.put("/v1/reporte-comercial/config", body);
      setCfg(data.config); toast.success("Configuración guardada"); loadCfg();
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); }
    finally { setBusy(""); }
  };
  const excel = async (s = suc) => {
    setBusy("xls");
    try {
      const resp = await api.get("/v1/reporte-comercial/excel", { params: { sucursal: s || undefined, equipo: eq || undefined }, responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `instalaciones_pendientes_${(s || "TODAS").replace(/\W+/g, "_")}${eq ? "_" + eq.replace(/\W+/g, "_") : ""}_${prev?.fecha || ""}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el Excel"); }
    finally { setBusy(""); }
  };
  const enviar = async (soloPrueba) => {
    if (soloPrueba && !pruebaA.trim()) return toast.error("Escriba el correo de prueba");
    setBusy(soloPrueba ? "test" : "send");
    try {
      const { data } = await api.post("/v1/reporte-comercial/enviar",
        { sucursal: suc || undefined, equipo: eq || undefined, solo_a: soloPrueba ? pruebaA : undefined }, { timeout: 300000 });
      if (data.ok) toast.success(`Enviado: ${(data.envios || []).map((x) => `${x.sucursal} (${x.filas})`).join(", ")}`);
      else toast.error(`Con errores: ${(data.errores || []).join(" · ")}`);
      loadRuns();
    } catch (e) { toast.error(errMsg(e, "No se pudo enviar")); }
    finally { setBusy(""); }
  };
  const verControl = async () => {
    setBusy("ctl");
    try { const { data } = await api.get("/v1/reporte-comercial/odoo-estado-control", { timeout: 60000 }); setCtl(data.columnas || []); }
    catch (e) { toast.error(errMsg(e, "No se pudo consultar Odoo")); }
    finally { setBusy(""); }
  };

  const card = "rounded-2xl border border-slate-200 bg-white";
  const lbl = "block text-[11px] font-bold uppercase text-slate-500 mb-1";

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="reporte-comercial">
        <div className="flex items-start gap-3">
          <Mail className="text-[#0A3747] mt-1" size={22} />
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0A3747]">Reporte Comercial · Instalaciones</h1>
            <p className="text-xs text-slate-500">Estado diario de cada venta nueva (instalación fibra / radio) por sucursal: desde que entra a flujo hasta que se instala o se cancela. Se envía por correo con un Excel (una hoja por sucursal).</p>
          </div>
        </div>

        {/* Configuración */}
        {cfg && (
          <div className={`${card} p-4 space-y-3`} data-testid="repcom-config">
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="repcom-enabled" />
                Envío automático diario
              </label>
              <div><span className={lbl}>Hora (Ecuador)</span>
                <input type="time" value={cfg.hora} onChange={(e) => setCfg({ ...cfg, hora: e.target.value })} className="border rounded px-2 py-1 text-sm" /></div>
              <div><span className={lbl}>Canceladas / instaladas de los últimos</span>
                <input type="number" min={1} max={60} value={cfg.ventana_dias} onChange={(e) => setCfg({ ...cfg, ventana_dias: e.target.value })} className="border rounded px-2 py-1 text-sm w-20" /> <span className="text-xs text-slate-500">días</span></div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!cfg.incluir_instalados} onChange={(e) => setCfg({ ...cfg, incluir_instalados: e.target.checked })} />
                Incluir instaladas
              </label>
            </div>
            <div>
              <span className={lbl}>Lista central (recibe TODAS las sucursales en un archivo)</span>
              <input value={destTxt["*"] || ""} onChange={(e) => setDestTxt({ ...destTxt, "*": e.target.value })}
                placeholder="comercial@… , gerencia@…" className="w-full border rounded px-2 py-1.5 text-sm" data-testid="repcom-central" />
            </div>
            <div>
              <span className={lbl}>Destinatarios por sucursal (cada lista recibe solo su sucursal)</span>
              <div className="grid md:grid-cols-2 gap-2">
                {sucursales.map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <span className="w-32 text-xs font-semibold text-slate-700 truncate" title={s}>{s}</span>
                    <input value={destTxt[s] || ""} onChange={(e) => setDestTxt({ ...destTxt, [s]: e.target.value })}
                      placeholder="correos separados por coma" className="flex-1 border rounded px-2 py-1 text-xs" />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className={lbl}>Destinatarios por equipo de ventas (cada lista recibe solo las OTs de su equipo, de todas las sucursales)</span>
              <div className="grid md:grid-cols-2 gap-2" data-testid="repcom-equipos">
                {equipos.length === 0 && <div className="text-xs text-slate-400">Aún no hay equipos de ventas en las OTs del reporte.</div>}
                {equipos.map((e) => (
                  <div key={e} className="flex items-center gap-2">
                    <span className="w-40 text-xs font-semibold text-slate-700 truncate" title={e}>{e}</span>
                    <input value={destEqTxt[e] || ""} onChange={(ev) => setDestEqTxt({ ...destEqTxt, [e]: ev.target.value })}
                      placeholder="correos separados por coma" className="flex-1 border rounded px-2 py-1 text-xs" />
                  </div>
                ))}
              </div>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold text-slate-600">«Pendiente aprobación» (estado de control de la venta en Odoo)</summary>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                <div><span className={lbl}>Columna en la suscripción</span>
                  <input value={cfg.control_column || ""} onChange={(e) => setCfg({ ...cfg, control_column: e.target.value })} className="border rounded px-2 py-1 text-xs w-56" placeholder="ej. control_state" /></div>
                <div className="flex-1"><span className={lbl}>Valores que significan «pendiente» (separados por coma)</span>
                  <input value={Array.isArray(cfg.control_pendiente) ? cfg.control_pendiente.join(", ") : (cfg.control_pendiente || "")}
                    onChange={(e) => setCfg({ ...cfg, control_pendiente: e.target.value })} className="w-full border rounded px-2 py-1 text-xs" /></div>
                <button onClick={verControl} disabled={busy === "ctl"} className="px-2 py-1 rounded border text-xs">{busy === "ctl" ? "Consultando…" : "Ver columnas en Odoo"}</button>
              </div>
              {ctl && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2 bg-slate-50">
                  {ctl.length === 0 && <div className="text-slate-500">Sin columnas candidatas.</div>}
                  {ctl.map((c) => (
                    <div key={c.columna} className="mb-1"><b>{c.columna}</b> <span className="text-slate-400">({c.tipo})</span>: {c.valores_90_dias.map((v) => `${v.valor ?? "(vacío)"}=${v.n}`).join(" · ")}</div>
                  ))}
                </div>
              )}
            </details>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={save} disabled={busy === "save"} className="px-3 py-1.5 rounded bg-[#0A3747] text-white text-xs font-bold inline-flex items-center gap-1" data-testid="repcom-save">
                {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar</button>
              {meta?.updated_at && <span className="text-[10px] text-slate-400">Último cambio: {meta.updated_by} · {fmt(meta.updated_at)}</span>}
            </div>
          </div>
        )}

        {/* Vista previa y acciones */}
        <div className={`${card} p-4 space-y-3`}>
          <div className="flex flex-wrap items-center gap-2">
            <select value={suc} onChange={(e) => { setSuc(e.target.value); loadPrev(e.target.value); }} className="border rounded px-2 py-1.5 text-sm" data-testid="repcom-sucursal">
              <option value="">Todas las sucursales</option>
              {sucursales.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={eq} onChange={(e) => { setEq(e.target.value); loadPrev(suc, e.target.value); }} className="border rounded px-2 py-1.5 text-sm" data-testid="repcom-equipo">
              <option value="">Todos los equipos de ventas</option>
              {equipos.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <button onClick={() => loadPrev()} className="px-2 py-1.5 rounded border text-xs inline-flex items-center gap-1">
              {busy === "prev" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar</button>
            <button onClick={() => excel()} disabled={busy === "xls"} className="px-2 py-1.5 rounded border text-xs inline-flex items-center gap-1" data-testid="repcom-excel">
              {busy === "xls" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Descargar Excel</button>
            <div className="flex items-center gap-1 ml-auto">
              <input value={pruebaA} onChange={(e) => setPruebaA(e.target.value)} placeholder="correo de prueba" className="border rounded px-2 py-1 text-xs w-48" />
              <button onClick={() => enviar(true)} disabled={!!busy} className="px-2 py-1.5 rounded border text-xs inline-flex items-center gap-1" data-testid="repcom-test">
                {busy === "test" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar prueba</button>
              <button onClick={() => enviar(false)} disabled={!!busy} className="px-2 py-1.5 rounded bg-amber-500 text-white text-xs font-bold inline-flex items-center gap-1" data-testid="repcom-send">
                {busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Reenviar a la lista</button>
            </div>
          </div>
          {prev && (
            <>
              <div className="flex flex-wrap gap-2">
                {estados.map((e) => (
                  <span key={e} className={`px-2 py-1 rounded text-[11px] font-semibold ${ESTADO_CLS[e] || "bg-slate-100"}`}>{e}: {prev.resumen?.[e] || 0}</span>
                ))}
                <span className="px-2 py-1 rounded text-[11px] font-bold bg-[#0A3747] text-white">Total: {prev.total}</span>
              </div>
              {(prev.notas || []).length > 0 && (
                <ul className="text-[11px] text-amber-700 list-disc pl-5">{prev.notas.map((n, i) => <li key={i}>{n}</li>)}</ul>
              )}
              <div className="overflow-x-auto border rounded-lg max-h-[480px]">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{["NÚMERO OT", "CONTRATO", "NOMBRE", "SUCURSAL", "EQUIPO DE VENTAS", "ESTADO", "FECHA DE AGENDAMIENTO", "HORA DE AGENDAMIENTO", "TECNICO AGENDADO", "ASESOR COMERCIAL", "MOTIVO CANCELACIÓN"].map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-bold text-slate-600 whitespace-nowrap">{h}</th>))}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {(prev.rows || []).map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-2 py-1 font-mono">{r["NÚMERO OT"]}</td><td className="px-2 py-1">{r.CONTRATO}</td>
                        <td className="px-2 py-1">{r.NOMBRE}</td><td className="px-2 py-1">{r.SUCURSAL}</td>
                        <td className="px-2 py-1">{r["EQUIPO DE VENTAS"]}</td>
                        <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded ${ESTADO_CLS[r.ESTADO] || ""}`}>{r.ESTADO}</span></td>
                        <td className="px-2 py-1">{r["FECHA DE AGENDAMIENTO"]}</td><td className="px-2 py-1">{r["HORA DE AGENDAMIENTO"]}</td>
                        <td className="px-2 py-1">{r["TECNICO AGENDADO"]}</td><td className="px-2 py-1">{r["ASESOR COMERCIAL"]}</td>
                        <td className="px-2 py-1">{r["MOTIVO CANCELACIÓN"]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {prev.total > (prev.rows || []).length && <div className="text-[10px] text-slate-400">Mostrando {(prev.rows || []).length} de {prev.total}. El Excel trae todas.</div>}
            </>
          )}
        </div>

        {/* Corridas */}
        <div className={`${card} p-4`}>
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-2 flex items-center gap-1"><Clock size={12} /> Últimas corridas</div>
          <div className="space-y-1">
            {runs.length === 0 && <div className="text-xs text-slate-400">Sin corridas aún.</div>}
            {runs.map((r) => (
              <div key={r.id} className="text-xs flex flex-wrap items-center gap-2 border-b border-slate-100 py-1">
                {r.ok ? <CheckCircle2 size={13} className="text-emerald-600" /> : r.finished_at ? <XCircle size={13} className="text-rose-600" /> : <Loader2 size={13} className="animate-spin text-slate-400" />}
                <span className="font-semibold">{r.fecha}</span>
                <span className="text-slate-500">{r.tipo === "auto" ? "automática" : `manual · ${r.solicitado_por || ""}`}</span>
                <span className="text-slate-400">{fmt(r.started_at)}</span>
                <span className="text-slate-600">{(r.detalle?.envios || []).map((x) => `${x.sucursal} (${x.filas})`).join(", ")}</span>
                {(r.detalle?.errores || []).length > 0 && <span className="text-rose-600">{r.detalle.errores.join(" · ")}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
